type IntersectParts<T extends readonly object[]> = T extends readonly [
    infer Head extends object,
    ...infer Rest extends object[],
]
    ? Head & IntersectParts<Rest>
    : unknown;

/**
 * Merges form parts into one object **without evaluating lazy getters**.
 *
 * Widget form assemblers expose FieldTree accessors as getters so the tree
 * paths materialise lazily — only after `syncFromEntity` has populated the
 * model (children of a FieldTree only exist for keys present in the model).
 * A plain object spread would defeat that: `{ ...part }` *calls* every getter
 * at spread time, freezing `undefined` FieldTree references into the result.
 * This composer copies **property descriptors** instead, so getters stay
 * getters and evaluate on first consumer access.
 *
 * Later parts override earlier ones key-by-key, mirroring spread semantics —
 * which is how a variant assembler installs e.g. the right position-plugin
 * flavour over a shared part.
 */
export function composeForm<Parts extends object[]>(...parts: [...Parts]): IntersectParts<Parts> {
    const target = {};
    for (const part of parts) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(part));
    }
    return target as IntersectParts<Parts>;
}
